export type Cents = number;
export type GarmentType = "tee" | "hoodie" | "crewneck" | "tote" | "sunshirt" | "hat";
export const SPOTS = ["front-center", "left-chest", "full-back"] as const;
export type Spot = (typeof SPOTS)[number];
export type Fulfillment = "pickup" | "ship";
// A shop (spec section 8): a row of pressroom.storefronts whose id is a slug, never a name the code knows.
export type StorefrontId = string;
export const SHOP_SLUG = /^[a-z][a-z0-9-]{1,30}$/;
export const isStorefrontId = (v: unknown): v is StorefrontId => typeof v === "string" && SHOP_SLUG.test(v);

// Garments with an S&S cost: (cost x blankMultiplier, to the cent, rounded up to roundUpToCents), per size, plus the
// print tier of every placement (prices.printTiers), is the base price. printPerGarmentCents is no longer used for them
// (kept, and still checked, for the record). A buyer on the designer sees it only as two prices: base x (1 + bankBps / 10000) and
// base x (1 + cardBps / 10000). Lives in settings (prices.rule), so Frank changes it without code.
export interface PriceRule {
  blankMultiplier: number;
  roundUpToCents: Cents;
  printPerGarmentCents: Cents;
  bankBps: number;
  cardBps: number;
}

// DTF transfers on the designer: a piece pays perSqInCents for each square inch of its box (never under minPieceCents),
// a gang sheet gangSheetCents; shipping a transfers request costs shippingCents flat (pickup is free).
export interface TransferPrices {
  perSqInCents: Cents;
  minPieceCents: Cents;
  gangSheetCents: Cents;
  shippingCents: Cents;
}

// What one print costs on a garment with an S&S cost, by its size (spec 2026-10-07 section 1): the first tier whose box
// holds both the print's width and its height. Sorted ascending; a print larger than the last box is refused.
export interface PrintTierSetting {
  name: string;
  maxWidthIn: number;
  maxHeightIn: number;
  // When every tier has it, the tier is picked by the print's area (square inches), so a tall or wide logo pays by its
  // real size (Frank 2026-10-07); the boxes then only describe the tier.
  maxAreaSqIn?: number;
  cents: Cents;
}

export interface PriceSettings {
  placeholder: boolean;
  garments: Record<GarmentType, { baseCents: Cents; upcharge2xlCents: Cents }>;
  print: { smallCents: Cents; mediumCents: Cents; largeCents: Cents };
  tiers: { minQty: number; discountPct: number }[];
  shipping: { baseCents: Cents; perExtraItemCents: Cents };
  rule: PriceRule;
  transfers: TransferPrices;
  printTiers: PrintTierSetting[];
  // What S&S charges to ship blanks in, passed on once per order that has any garment priced from an S&S cost: cents,
  // waived when the order's goods total (subtotal less the bulk discount, before outbound shipping and before the two
  // prices) reaches waiveAtCents. Absent: BLANK_SHIPPING_DEFAULT in src/lib/pricing.ts ($10.00, free at $200.00).
  blankShipping?: BlankShipping;
  // Sales tax (decision 23): rateBp on the taxable subtotal after discount, for merch, custom or both, with shipping
  // only when taxShipping. Added at checkout as its own line; statements use sales before it.
  tax: TaxSetting;
}

export interface TaxSetting {
  rateBp: number;
  appliesTo: "merch" | "custom" | "both";
  taxShipping: boolean;
}

export interface BlankShipping {
  cents: Cents;
  waiveAtCents: Cents;
}

// What a client order page may see: the rule and the transfer prices stay on the server.
export type PublicPrices = Omit<PriceSettings, "rule" | "transfers">;

export interface PrintRules {
  dpi: number;
  mirror: boolean;
  alphaThreshold: number;
  // The film's width, which is also a gang sheet's width.
  printerMaxWidthIn: number;
  // The longest piece the printer runs, which is also a gang sheet's height.
  maxLengthIn: number;
  confirmedByTestPrint: boolean;
  // Temperature, time, pressure and peel for pressing a transfer. Blank until Frank's test press; emails leave it out
  // while it is blank.
  pressInstructions: string;
}

export interface Timing {
  changeReminderDays: number;
  changeCancelDays: number;
  heartbeatTimeoutMinutes: number;
  unpaidExpiryHours: number;
  // Card holds on unapproved orders: renewed after reauthAfterDays, flagged in Slack at authWarnDays, cancelled at
  // authCancelDays (days since the order was placed).
  reauthAfterDays: number;
  authWarnDays: number;
  authCancelDays: number;
  // A pending bank debit is asked for its status this often when no webhook arrived.
  bankPollMinutes: number;
}

// One shop's settings: its prices and outside printer fee (shop_pricing, Task A5), the print rules and timing
// (settings; Task A6 moves print rules to shop_press).
export interface Settings {
  prices: PriceSettings;
  printRules: PrintRules;
  timing: Timing;
  // The fee this shop owes the shop that prints for it (today's production_fee rule); null when it prints its own.
  outsidePrinterFee: ProductionFeeDefaults | null;
}

export interface PriceOverride {
  garments?: Partial<Record<GarmentType, Partial<{ baseCents: Cents; upcharge2xlCents: Cents }>>>;
  print?: Partial<PriceSettings["print"]>;
  tiers?: PriceSettings["tiers"];
}

// Kenny's fee on an order whose client has no fee of its own (settings.production_fee): printSharePct percent of the
// print revenue after the bulk discount on garments with an S&S cost, transferPct percent of a transfer order's
// subtotal after discount, and, for hand-registered (type-table) garments only, perGarmentCents a garment plus
// perExtraPlacementCents for each print location past the first. ownGarmentPrintSharePct is for customer-supplied
// garments (spec 2026-10-07 section 2).
export interface ProductionFeeDefaults {
  perGarmentCents: Cents;
  perExtraPlacementCents: Cents;
  transferPct: number;
  printSharePct: number;
  ownGarmentPrintSharePct: number;
  // How a garment priced by print tier pays Kenny: "share" (absent) = printSharePct of the order's stored print
  // revenue; "flat" = perGarmentCents a garment plus perExtraPlacementCents for each print past its first, the same
  // rule as a hand-registered garment (Frank 2026-10-08: $5 a print and press, $7 with a second print).
  costedGarmentRule?: "share" | "flat";
}

export type ProductionFee = { mode: "per_garment"; cents: Cents } | { mode: "percent"; percent: number } | null;
